// Web UI: bookmark list and reader.
//
// Vanilla ES modules, no build step — the API serves /shared, so the list
// rendering and API client here are the same code the extension sidebar uses
// (see shared/README.md).
import { docType, filterBookmarks, renderTagChips, type DocType } from '../../ui/shared/bookmark-list.ts';
import { el, formatDate, hostname } from '../../ui/shared/dom.ts';
import {
  api, state, TOKEN_KEY, USER_KEY,
  setBookmarkPatchListener, setUnauthorizedHandler,
} from './store.ts';
import { openReader, closeReader } from './reader.ts';
import { openHighlightsView, closeHighlightsView } from './highlights.ts';
import { openTrashView, closeTrashView } from './trash.ts';
import { renderFolderedList, createFolder, moveBookmarks } from './folders.ts';
import type { Bookmark, ReadingStatus } from '../../ui/shared/types.ts';

// ── Elements ──────────────────────────────────────────────────────────────────

/**
 * Look up an element that the page is expected to contain.
 *
 * Throws rather than returning null: every id here is in index.html, so a
 * miss means the markup and the script have diverged — which is exactly the
 * failure that should be loud rather than a silent no-op further down.
 */
function $<T extends HTMLElement = HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element #${id}`);
  return found as T;
}

const authView = $('auth-view');
const appView = $('app-view');
const readerView = $('reader-view');
const highlightsView = $('highlights-view');
const trashView = $('trash-view');

// ── Views ─────────────────────────────────────────────────────────────────────

function show(view: HTMLElement): void {
  for (const v of [authView, appView, readerView, highlightsView, trashView]) {
    v.classList.toggle('hidden', v !== view);
    v.classList.toggle('active', v === view);
  }
  // Highlights and the trash reuse the reader's card styling (annotation-card
  // etc.), which is themed off the same --reader-bg/--reader-fg tokens as the
  // reader body.
  document.body.classList.toggle('reading', view === readerView || view === highlightsView || view === trashView);
  if (view !== appView) hideUndo();
}

// ── Undo ──────────────────────────────────────────────────────────────────────

// A delete only moves bookmarks to the trash, so it isn't confirmed up front;
// instead this offers the way back for a few seconds. Later, it's the trash.
const UNDO_MS = 8000;
let undoTimer: ReturnType<typeof setTimeout> | undefined;
let undoAction: (() => Promise<void>) | null = null;

function showUndo(message: string, undo: () => Promise<void>): void {
  clearTimeout(undoTimer);
  undoAction = undo;
  $('undo-message').textContent = message;
  $('undo-toast').hidden = false;
  undoTimer = setTimeout(hideUndo, UNDO_MS);
}

function hideUndo(): void {
  clearTimeout(undoTimer);
  undoAction = null;
  $('undo-toast').hidden = true;
}

async function runUndo(): Promise<void> {
  const undo = undoAction;
  hideUndo();
  if (!undo) return;
  try {
    await undo();
  } catch (err) {
    alert((err as Error).message);
  }
  await loadBookmarks();
}

/** Restore each of `ids` from the trash; all are tried even if one fails. */
async function restoreAll(ids: number[]): Promise<void> {
  const results = await Promise.allSettled(ids.map((id) => api.restoreBookmark(id)));
  const failed = results.filter((r) => r.status === 'rejected').length;
  if (failed) throw new Error(`Could not restore ${failed} bookmark(s) — they are still in the trash.`);
}

function showAuth(): void {
  show(authView);
  $(FOCUS_FIELD[authMode]).focus();
}

function showApp(): void {
  $('user-display').textContent = localStorage.getItem(USER_KEY) || '';
  show(appView);
}

/** Forget the session locally. Also what a 401 from the server triggers. */
function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  state.bookmarks = [];
  state.tags = [];
  state.folders = [];
  if (window.location.hash) window.location.hash = '';
  showAuth();
}

function logout(): void {
  // signOut reads the token as it starts, before this returns, so the local
  // copy can go straight away instead of the user waiting on the network. The
  // server-side revoke is what makes a copied token stop working.
  void api.signOut();
  clearSession();
}

// ── Auth ──────────────────────────────────────────────────────────────────────

// One form serves four jobs; the mode decides which fields and buttons show.
type AuthMode = 'login' | 'register' | 'forgot' | 'reset';
let authMode: AuthMode = 'login';
/** From the emailed reset link (?token=), held until the new password is submitted. */
let resetToken: string | null = null;

const FOCUS_FIELD: Record<AuthMode, string> = {
  login: 'username', register: 'username', forgot: 'email', reset: 'password',
};
const SUBMIT_LABEL: Record<AuthMode, string> = {
  login: '[ Login ]', register: '[ Create account ]', forgot: '[ Send reset link ]', reset: '[ Set password ]',
};

function clearAuthMessages(): void {
  $('auth-error').classList.add('hidden');
  $('auth-notice').classList.add('hidden');
}

function setAuthMode(mode: AuthMode): void {
  authMode = mode;
  clearAuthMessages();
  $('username-group').classList.toggle('hidden', mode === 'forgot' || mode === 'reset');
  $('email-group').classList.toggle('hidden', mode !== 'register' && mode !== 'forgot');
  $('password-group').classList.toggle('hidden', mode === 'forgot');
  $('password-label').textContent = mode === 'reset' ? 'New password:' : 'Password:';
  $<HTMLInputElement>('password').autocomplete = mode === 'login' ? 'current-password' : 'new-password';
  $('login-btn').textContent = SUBMIT_LABEL[mode];
  $('mode-btn').textContent = mode === 'login' ? '[ Register ]' : '[ Back to login ]';
  $('mode-btn').classList.toggle('hidden', mode === 'reset');
  $('forgot-btn').classList.toggle('hidden', mode !== 'login');
}

function authError(message: string): void {
  const box = $('auth-error');
  box.textContent = `[ERROR]: ${message}`;
  box.classList.remove('hidden');
}

function authNotice(message: string): void {
  const box = $('auth-notice');
  box.textContent = message;
  box.classList.remove('hidden');
}

const field = (id: string): string => $<HTMLInputElement>(id).value.trim();

async function startSession(token: string, username: string): Promise<void> {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, username);

  const redirect = new URLSearchParams(window.location.search).get('redirect');
  if (redirect) {
    window.location.href = `${redirect}#token=${encodeURIComponent(token)}&user=${encodeURIComponent(username)}`;
    return;
  }

  for (const id of ['username', 'email', 'password']) $<HTMLInputElement>(id).value = '';
  setAuthMode('login');
  await boot();
}

async function doLogin(): Promise<void> {
  const username = field('username');
  const password = $<HTMLInputElement>('password').value;
  if (!username || !password) throw new Error('Credentials required');

  const { token, user } = await api.signIn(username, password);
  await startSession(token, user.displayUsername || user.username || username);
}

async function doRegister(): Promise<void> {
  const username = field('username');
  const email = field('email');
  const password = $<HTMLInputElement>('password').value;
  if (!username || !email || !password) throw new Error('Username, email and password required');

  const { token, user } = await api.signUp({ username, email, password });
  if (!token) {
    // The server is set to require a confirmed address before the first login.
    setAuthMode('login');
    authNotice('Check your email to confirm your address, then log in.');
    return;
  }
  await startSession(token, user.displayUsername || user.username || username);
}

async function doForgot(): Promise<void> {
  const email = field('email');
  if (!email) throw new Error('Email required');

  await api.requestPasswordReset(email, `${window.location.origin}/?reset=1`);
  // Same answer whether or not the address has an account.
  setAuthMode('login');
  authNotice('If that address has an account, a reset link is on its way.');
}

async function doReset(): Promise<void> {
  const password = $<HTMLInputElement>('password').value;
  if (!resetToken) throw new Error('This reset link is no longer valid — request a new one.');
  if (!password) throw new Error('New password required');

  await api.resetPassword(resetToken, password);
  resetToken = null;
  $<HTMLInputElement>('password').value = '';
  window.history.replaceState(null, '', `${window.location.origin}/`); // drop ?token=
  setAuthMode('login');
  authNotice('Password updated — log in with the new one.');
}

async function submitAuth(event: Event): Promise<void> {
  event.preventDefault();
  clearAuthMessages();
  try {
    if (authMode === 'login') await doLogin();
    else if (authMode === 'register') await doRegister();
    else if (authMode === 'forgot') await doForgot();
    else await doReset();
  } catch (err) {
    authError((err as Error).message);
  }
}

/** Landing from an emailed reset link: /?reset=1&token=… (or &error=… if it was bad). */
function openResetLinkIfPresent(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('reset')) return;
  const token = params.get('token');
  if (token) {
    resetToken = token;
    setAuthMode('reset');
  } else {
    authError('That reset link is invalid or has expired — request a new one.');
  }
}

// ── Bookmarks ─────────────────────────────────────────────────────────────────

async function loadBookmarks(): Promise<void> {
  const list = $('bookmarks-list');
  try {
    state.bookmarks = await api.getBookmarks();
  } catch (err) {
    if ((err as { status?: number }).status === 401 || (err as { status?: number }).status === 403) return;
    list.replaceChildren(el('div', { class: 'msg error-msg' }, (err as Error).message));
    return;
  }

  // Tags and folders are best-effort: a failure there shouldn't blank the
  // list. Without folders, every bookmark shows at the top level.
  const [tags, folders] = await Promise.allSettled([api.getTags(), api.getFolders()]);
  state.tags = tags.status === 'fulfilled' ? tags.value : [];
  state.folders = folders.status === 'fulfilled' ? folders.value : [];

  renderTags();
  renderList();
}

function renderTags(): void {
  renderTagChips($('tag-filter-bar'), state.tags, {
    activeTag: state.activeTag,
    onSelectTag: (tag) => {
      state.activeTag = state.activeTag === tag ? null : tag;
      renderTags();
      renderList();
    },
  });
}

function renderList(): void {
  const items = filterBookmarks(state.bookmarks, {
    query: state.query,
    tag: state.activeTag,
    status: state.activeStatus || null,
    type: state.activeType || null,
  });

  const visible = renderFolderedList($('bookmarks-list'), items, {
    filtering: Boolean(state.query.trim() || state.activeTag || state.activeStatus || state.activeType),
    onChange: renderList,
    emptyMessage: state.bookmarks.length ? 'no matches.' : 'Empty',
    selectedIds: state.selected,
    onToggleSelect: (bookmark, checked) => {
      checked ? state.selected.add(bookmark.id) : state.selected.delete(bookmark.id);
      renderBulkBar();
      // Toggle the row's own styling without rebuilding the list, so the
      // checkbox keeps focus for keyboard use.
      $('bookmarks-list')
        .querySelector(`.bm-item[data-id="${bookmark.id}"]`)
        ?.classList.toggle('selected', checked);
    },
    onOpenReader: (bookmark) => {
      window.location.hash = `#/read/${bookmark.id}`;
    },
    onSelectTag: (tag) => {
      state.activeTag = state.activeTag === tag ? null : tag;
      renderTags();
      renderList();
    },
    onDelete: async (bookmark) => {
      try {
        await api.deleteBookmark(bookmark.id);
        state.bookmarks = state.bookmarks.filter((b) => b.id !== bookmark.id);
        if (previewedId === bookmark.id) closePreview();
        showUndo(`Moved "${bookmark.title || bookmark.url}" to the trash.`, () => restoreAll([bookmark.id]));
        state.tags = await api.getTags().catch(() => state.tags);
        renderTags();
        renderList();
      } catch (err) {
        alert((err as Error).message);
      }
    },
  });

  // Drop selections that are no longer visible (filtered out, or in a
  // collapsed folder), so a bulk action can't silently hit a bookmark the
  // user can't see.
  for (const id of state.selected) if (!visible.has(id)) state.selected.delete(id);
  renderBulkBar();
  renderSidebar();
  markPreviewed();
}

// ── Sidebar and preview ───────────────────────────────────────────────────────

/** The bookmark whose preview is open in the side column, if any. */
let previewedId: number | null = null;

/**
 * The counts on the status and type buttons. They describe the whole library,
 * not the filtered view, so picking a filter doesn't change the numbers beside
 * the others. A bookmark with no reading row is unread, as on the server.
 */
function renderSidebar(): void {
  const statusCounts = new Map<string, number>([['', state.bookmarks.length]]);
  const typeCounts = new Map<string, number>([['', state.bookmarks.length]]);
  for (const bookmark of state.bookmarks) {
    const status = bookmark.reading_status || 'unread';
    statusCounts.set(status, (statusCounts.get(status) ?? 0) + 1);
    const type = docType(bookmark);
    typeCounts.set(type, (typeCounts.get(type) ?? 0) + 1);
  }
  for (const button of $('status-filter').querySelectorAll<HTMLElement>('[data-status]')) {
    setButtonCount(button, statusCounts.get(button.dataset.status ?? '') ?? 0);
  }
  for (const button of $('type-filter').querySelectorAll<HTMLElement>('[data-type]')) {
    setButtonCount(button, typeCounts.get(button.dataset.type ?? '') ?? 0);
  }
}

function setButtonCount(button: HTMLElement, count: number): void {
  const label = button.querySelector('.count');
  if (label) label.textContent = String(count);
}

/**
 * Show a bookmark's summary in the side column. Clicking the same row again
 * leaves it open; the close button and Escape dismiss it.
 */
function openPreview(bookmark: Bookmark): void {
  previewedId = bookmark.id;
  const preview = $('preview');
  const percent = Math.round((bookmark.reading_percent || 0) * 100);
  const status = `${bookmark.reading_status || 'unread'}${percent ? ` · ${percent}%` : ''}`;
  // Only plain web links get an "original" button; an upload's URL goes nowhere.
  const isWebLink = /^https?:\/\//i.test(bookmark.url);

  preview.replaceChildren(
    el('div', { class: 'sidebar-heading preview-head' }, [
      '--- Preview ---',
      el('button', { type: 'button', class: 'btn-sm', 'aria-label': 'Close preview', onclick: closePreview }, '[ x ]'),
    ]),
    el('div', { class: 'preview-title' }, bookmark.title || bookmark.url),
    el('div', { class: 'hint' }, `${hostname(bookmark.url)} · ${formatDate(bookmark.created_at)}`),
    el('div', { class: 'hint' }, status),
    // Text only: the excerpt is article content and must never become markup.
    bookmark.excerpt
      ? el('p', { class: 'preview-excerpt' }, bookmark.excerpt)
      : el('p', { class: 'hint' }, 'No summary: this bookmark has no archived article.'),
    ...(bookmark.tags.length ? [el('div', { class: 'hint' }, `tags: ${bookmark.tags.join(', ')}`)] : []),
    el('div', { class: 'preview-actions' }, [
      el('button', {
        type: 'button',
        class: 'btn-sm',
        onclick: () => { window.location.hash = `#/read/${bookmark.id}`; },
      }, '[ read ]'),
      ...(isWebLink
        ? [el('a', { class: 'btn-sm', href: bookmark.url, target: '_blank', rel: 'noopener noreferrer' }, '[ original ]')]
        : []),
    ]),
  );
  preview.classList.remove('hidden');
  $('app-body').classList.add('has-preview');
  markPreviewed();
}

function closePreview(): void {
  previewedId = null;
  const preview = $('preview');
  preview.classList.add('hidden');
  preview.replaceChildren();
  $('app-body').classList.remove('has-preview');
  markPreviewed();
}

/** Highlight the previewed row. Rows are rebuilt on each render, so this runs after every one. */
function markPreviewed(): void {
  const list = $('bookmarks-list');
  for (const row of list.querySelectorAll('.bm-item.previewing')) row.classList.remove('previewing');
  if (previewedId !== null) {
    list.querySelector(`.bm-item[data-id="${previewedId}"]`)?.classList.add('previewing');
  }
}

// ── Bulk actions ──────────────────────────────────────────────────────────────

function renderBulkBar(): void {
  const bar = $('bulk-bar');
  const count = state.selected.size;
  bar.hidden = count === 0;
  $('bulk-count').textContent = `${count} selected`;

  const move = $<HTMLSelectElement>('bulk-move');
  move.replaceChildren(
    el('option', { value: '', selected: true, disabled: true }, 'move to…'),
    el('option', { value: 'top' }, '(no folder)'),
    ...state.folders.map((f) => el('option', { value: String(f.id) }, f.name)),
  );
}

async function moveSelected(): Promise<void> {
  const move = $<HTMLSelectElement>('bulk-move');
  const ids = [...state.selected];
  if (!ids.length || !move.value) return;
  try {
    await moveBookmarks(ids, move.value === 'top' ? null : Number(move.value));
    state.selected.clear();
  } catch (err) {
    alert((err as Error).message);
  }
  renderList();
}

async function exportDigest(): Promise<void> {
  const ids = [...state.selected];
  if (!ids.length) return;

  const button = $<HTMLButtonElement>('bulk-export');
  button.disabled = true;
  button.textContent = '[ exporting... ]';
  try {
    // Fetched rather than linked because the endpoint needs an auth header,
    // which a plain <a href> can't send.
    const markdown = await api.request<string>('/export/digest', {
      method: 'POST',
      body: { ids },
      raw: true,
    });
    downloadText(markdown, `digest-${new Date().toISOString().slice(0, 10)}.md`);
  } catch (err) {
    alert((err as Error).message);
  } finally {
    button.disabled = false;
    button.textContent = '[ export digest ]';
  }
}

function downloadText(text: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function deleteSelected(): Promise<void> {
  const ids = [...state.selected];
  if (!ids.length) return;

  const deleted: number[] = [];
  for (const id of ids) {
    try {
      await api.deleteBookmark(id);
      deleted.push(id);
    } catch { /* counted below */ }
  }

  state.selected.clear();
  await loadBookmarks();
  if (deleted.length) {
    showUndo(`Moved ${deleted.length} bookmark${deleted.length === 1 ? '' : 's'} to the trash.`, () => restoreAll(deleted));
  }
  if (deleted.length < ids.length) alert(`Could not delete ${ids.length - deleted.length} bookmark(s).`);
}

async function addBookmark(event: Event): Promise<void> {
  event.preventDefault();
  const status = $('add-status');
  const url = $<HTMLInputElement>('url').value.trim();
  if (!url) return;

  const payload = {
    url,
    title: $<HTMLInputElement>('title').value.trim() || undefined,
    tags: $<HTMLInputElement>('tags').value.split(',').map((t) => t.trim()).filter(Boolean),
    archive: $<HTMLInputElement>('archive-toggle').checked,
  };

  status.textContent = payload.archive ? 'fetching article...' : 'saving...';
  status.classList.remove('hidden', 'error-msg');

  try {
    const bookmark = await api.addBookmark(payload);
    $<HTMLInputElement>('url').value = '';
    $<HTMLInputElement>('title').value = '';
    $<HTMLInputElement>('tags').value = '';

    // The save succeeds even when extraction doesn't, so report them apart.
    const article = bookmark.article;
    if (article?.content_type === 'youtube' && !article.has_content && !article.extract_error) {
      status.textContent = 'saved — the transcript will come from the browser extension';
      setTimeout(() => status.classList.add('hidden'), 4000);
    } else if ((payload.archive || article?.content_type === 'youtube') && !article?.has_content) {
      status.textContent = `saved — not archived: ${article?.extract_error || 'unknown error'}`;
    } else {
      status.textContent = 'saved.';
      setTimeout(() => status.classList.add('hidden'), 2500);
    }
    await loadBookmarks();
  } catch (err) {
    status.textContent = (err as Error).message;
    status.classList.add('error-msg');
  }
}

/** Upload each chosen file in turn, reporting which (if any) were rejected. */
async function uploadFiles(event: Event): Promise<void> {
  event.preventDefault();
  const status = $('upload-status');
  const input = $<HTMLInputElement>('upload-file');
  const files = [...(input.files ?? [])];
  if (!files.length) return;

  const tags = $<HTMLInputElement>('upload-tags').value.split(',').map((t) => t.trim()).filter(Boolean);
  const failures: string[] = [];
  status.classList.remove('hidden', 'error-msg');

  for (const [i, file] of files.entries()) {
    status.textContent = `uploading ${file.name} (${i + 1}/${files.length})...`;
    try {
      await api.uploadFile(file, { tags });
    } catch (err) {
      failures.push(`${file.name}: ${(err as Error).message}`);
    }
  }

  if (failures.length < files.length) {
    input.value = '';
    $<HTMLInputElement>('upload-tags').value = '';
    await loadBookmarks();
  }

  if (failures.length) {
    status.textContent = failures.join('\n');
    status.classList.add('error-msg');
  } else {
    status.textContent = 'saved.';
    setTimeout(() => status.classList.add('hidden'), 2500);
  }
}

/**
 * Save pasted text as a bookmark. It goes through the same pipeline as an
 * uploaded .txt/.md file, so the server needs nothing new; the made-up
 * filename only names the type and the fallback title.
 */
async function savePastedText(event: Event): Promise<void> {
  event.preventDefault();
  const status = $('paste-status');
  const textInput = $<HTMLTextAreaElement>('paste-text');
  const titleInput = $<HTMLInputElement>('paste-title');
  const tagsInput = $<HTMLInputElement>('paste-tags');
  const text = textInput.value;
  if (!text.trim()) return;

  const title = titleInput.value.trim();
  const markdown = $<HTMLInputElement>('paste-markdown').checked;
  const firstLine = text.trim().split('\n')[0]!.replace(/^#+\s*/, '').trim();
  // Slashes would be read as a path by the server and cut the name short.
  const name = (title || firstLine || 'Pasted text').replace(/[\\/]/g, '-').slice(0, 80).trim();
  const file = new File([text], `${name}.${markdown ? 'md' : 'txt'}`, { type: 'text/plain' });
  const tags = tagsInput.value.split(',').map((t) => t.trim()).filter(Boolean);

  status.classList.remove('hidden', 'error-msg');
  status.textContent = 'saving...';
  try {
    await api.uploadFile(file, { title: title || undefined, tags });
    textInput.value = '';
    titleInput.value = '';
    tagsInput.value = '';
    status.textContent = 'saved.';
    setTimeout(() => status.classList.add('hidden'), 2500);
    await loadBookmarks();
  } catch (err) {
    status.textContent = (err as Error).message;
    status.classList.add('error-msg');
  }
}

// ── Routing ───────────────────────────────────────────────────────────────────

// Hash routing keeps this a single static page — no server rewrite rules, and
// the API stays a plain file server for /public.
async function route(): Promise<void> {
  if (!localStorage.getItem(TOKEN_KEY)) {
    showAuth();
    return;
  }

  const match = /^#\/read\/(\d+)$/.exec(window.location.hash);
  if (match) {
    closeHighlightsView();
    closeTrashView();
    show(readerView);
    await openReader(Number(match[1]!));
    return;
  }

  if (window.location.hash === '#/highlights') {
    closeReader();
    closeTrashView();
    show(highlightsView);
    await openHighlightsView();
    return;
  }

  if (window.location.hash === '#/trash') {
    closeReader();
    closeHighlightsView();
    show(trashView);
    // A restored bookmark should be in the list on the way back.
    await openTrashView(() => { void loadBookmarks(); });
    return;
  }

  closeReader();
  closeHighlightsView();
  closeTrashView();
  showApp();
  if (!state.bookmarks.length) await loadBookmarks();
}

// ── Boot ──────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  if (!localStorage.getItem(TOKEN_KEY)) {
    showAuth();
    return;
  }
  showApp();
  await loadBookmarks();
  await route();
}

setUnauthorizedHandler(clearSession);
// Re-render only when the list is actually on screen; the reader patches
// bookmarks as you scroll, and rebuilding a hidden list each time is waste.
setBookmarkPatchListener(() => {
  if (!document.body.classList.contains('reading')) renderList();
});

$('auth-form').addEventListener('submit', submitAuth);
$('mode-btn').addEventListener('click', () => setAuthMode(authMode === 'login' ? 'register' : 'login'));
$('forgot-btn').addEventListener('click', () => setAuthMode('forgot'));
$('logout-btn').addEventListener('click', logout);
$('highlights-nav').addEventListener('click', () => { window.location.hash = '#/highlights'; });
$('trash-nav').addEventListener('click', () => { window.location.hash = '#/trash'; });
$('undo-btn').addEventListener('click', runUndo);

// The extension downloads: under the login form, and behind [ Extension ] once signed in.
for (const slot of document.querySelectorAll('.extension-slot')) {
  slot.append($<HTMLTemplateElement>('extension-info').content.cloneNode(true));
}
$('extension-nav').addEventListener('click', () => {
  const open = $('app-extension').classList.toggle('hidden');
  $('extension-nav').setAttribute('aria-expanded', String(!open));
});
$('add-form').addEventListener('submit', addBookmark);
$('upload-form').addEventListener('submit', uploadFiles);
$('paste-form').addEventListener('submit', savePastedText);

$('bulk-export').addEventListener('click', exportDigest);
$('bulk-delete').addEventListener('click', deleteSelected);
$('bulk-move').addEventListener('change', moveSelected);
$('new-folder').addEventListener('click', createFolder);
$('bulk-clear').addEventListener('click', () => {
  state.selected.clear();
  renderList();
});

$('search').addEventListener('input', (e: Event) => {
  state.query = (e.target as HTMLInputElement).value;
  renderList();
});

$('status-filter').addEventListener('click', (e: Event) => {
  const button = (e.target as HTMLElement).closest<HTMLElement>('[data-status]');
  if (!button) return;
  state.activeStatus = (button.dataset.status ?? '') as ReadingStatus | '';
  for (const b of $('status-filter').querySelectorAll('[data-status]')) {
    b.classList.toggle('active', b === button);
  }
  renderList();
});

$('type-filter').addEventListener('click', (e: Event) => {
  const button = (e.target as HTMLElement).closest<HTMLElement>('[data-type]');
  if (!button) return;
  state.activeType = (button.dataset.type ?? '') as DocType | '';
  for (const b of $('type-filter').querySelectorAll('[data-type]')) {
    b.classList.toggle('active', b === button);
  }
  renderList();
});

// A click on a row's own text (not a link, button or checkbox) previews it.
// The title link keeps opening the original, as it always has.
$('bookmarks-list').addEventListener('click', (e: Event) => {
  const target = e.target as HTMLElement;
  if (target.closest('a, button, input, label, select, textarea')) return;
  const id = Number(target.closest<HTMLElement>('.bm-item')?.dataset.id);
  const bookmark = state.bookmarks.find((b) => b.id === id);
  if (bookmark) openPreview(bookmark);
});

document.addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === 'Escape' && previewedId !== null) closePreview();
});

window.addEventListener('hashchange', route);

openResetLinkIfPresent();
boot();
